import React, { useState, useEffect } from 'react';
import { Card, Button, Result, Typography, Row, Col, Statistic, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const ExamResult = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [examData, setExamData] = useState(null);

  // 从API获取最近的考试记录
  useEffect(() => {
    const fetchLatestExam = async () => {
      try {
        console.log('正在获取最新考试记录...');
        
        const res = await axios.get('/api/exams/history');
        console.log('获取到考试历史:', res.data);
        
        if (res.data && Array.isArray(res.data) && res.data.length > 0) {
          // 根据创建时间排序
          const sortedExams = [...res.data].sort((a, b) => {
            const dateA = new Date(b.createdAt || 0);
            const dateB = new Date(a.createdAt || 0);
            return dateA - dateB;
          });
          
          const latestExam = sortedExams[0];
          console.log('最新考试记录:', latestExam);
          
          // 适配数据库格式
          const examScore = Number(latestExam.totalScore || 0);
          const totalQuestions = 50; // 默认50题
          const correctAnswers = Math.round(examScore / 2); // 假设每题2分
          const passScore = 80;
          const isPassed = latestExam.status === '通过';
          
          // 格式化日期
          let examDateStr = '未知';
          try {
            if (latestExam.createdAt) {
              examDateStr = new Date(latestExam.createdAt).toLocaleString();
            }
          } catch (err) {
            console.error('日期格式化错误:', err);
            examDateStr = '日期格式有误';
          }
          
          // 构造考试结果数据，适配数据库字段
          const resultData = {
            score: examScore,
            professionalScore: Number(latestExam.professionalScore || 0),
            fireSafetyScore: Number(latestExam.fireSafetyScore || 0),
            totalQuestions: totalQuestions,
            correctAnswers: correctAnswers,
            passScore: passScore,
            passed: isPassed,
            category: latestExam.professionalCategory || '未知',
            examDate: examDateStr
          };
          
          console.log('构造的结果数据:', resultData);
          setExamData(resultData);
          setError(null);
        } else {
          throw new Error('未找到考试记录');
        }
      } catch (err) {
        console.error('获取考试记录失败:', err);
        setError('无法获取考试结果数据');
        message.error('获取考试结果失败: ' + (err.response?.data?.msg || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchLatestExam();
  }, []);

  // 显示加载状态
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column' }}>
        <Spin size="large" />
        <Text style={{ marginTop: 20 }}>正在加载考试结果...</Text>
      </div>
    );
  }

  // 显示错误状态
  if (error || !examData) {
    return (
      <Result
        status="warning"
        title="无法显示考试结果"
        subTitle={error || "考试结果数据不可用"}
        extra={[
          <Button 
            type="primary" 
            key="history" 
            onClick={() => navigate('/history')}
          >
            查看考试历史
          </Button>,
          <Button 
            key="selection" 
            onClick={() => navigate('/selection')}
          >
            返回考试中心
          </Button>
        ]}
      />
    );
  }

  // 显示考试结果
  const { 
    score, 
    professionalScore,
    fireSafetyScore,
    totalQuestions, 
    correctAnswers, 
    passScore, 
    passed,
    category,
    examDate
  } = examData;
  
  const passStatus = passed ? 'success' : 'error';
  const passText = passed ? '恭喜，您已通过考试！' : '很遗憾，您未通过考试';
  
  // 计算正确率，避免NaN
  const correctRate = totalQuestions > 0 
    ? ((correctAnswers / totalQuestions) * 100).toFixed(1) 
    : 0;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2}>考试结果</Title>
      
      <Card>
        <Result
          status={passStatus}
          title={passText}
          subTitle={`您的得分：${score}分，通过分数线：${passScore}分`}
        />
        
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Text strong>考试类别: {category || '未知'}</Text>
          {examDate && <div><Text>考试时间: {examDate}</Text></div>}
        </div>
        
        <Row gutter={16} style={{ textAlign: 'center', marginBottom: 24 }}>
          <Col span={8}>
            <Statistic title="总题目数" value={totalQuestions || 0} />
          </Col>
          <Col span={8}>
            <Statistic title="正确答题数" value={correctAnswers || 0} />
          </Col>
          <Col span={8}>
            <Statistic 
              title="正确率" 
              value={correctRate} 
              suffix="%" 
            />
          </Col>
        </Row>
        
        <Row gutter={16} style={{ textAlign: 'center', marginBottom: 24 }}>
          <Col span={12}>
            <Statistic title="专业知识得分" value={professionalScore || 0} suffix="分" />
          </Col>
          <Col span={12}>
            <Statistic title="消防安全得分" value={fireSafetyScore || 0} suffix="分" />
          </Col>
        </Row>
        
        <div style={{ textAlign: 'center' }}>
          <Button type="primary" onClick={() => navigate('/history')}>
            查看考试历史
          </Button>
          <Button style={{ marginLeft: 16 }} onClick={() => navigate('/selection')}>
            返回考试中心
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ExamResult; 